import { useCallback } from 'react';
import { impersonationService } from './impersonationService';
import { setStoredImpersonation, getStoredImpersonation, clearStoredImpersonation } from './impersonationStorage';
import { appNotify } from '../../utils';
import { clearPersistedQueryCache } from '../../services/core/queryPersister';

/**
 * Старт/стоп эмуляции всегда завершается полной перезагрузкой страницы.
 * Так проще и надёжнее, чем точечно инвалидировать React Query кэш и
 * переподключать WebSocket.
 *
 * Одного reload НЕДОСТАТОЧНО: часть кэша (в т.ч. префикс 'rbac' — права
 * пользователя) персистится в localStorage и переживает перезагрузку, поэтому
 * перед reload его нужно явно сбросить — иначе новая личность стартует
 * с правами предыдущей. См. services/core/queryPersister.ts.
 */
export function useStartImpersonation() {
  return useCallback(async (userId: string, displayName: string): Promise<void> => {
    try {
      const result = await impersonationService.start(userId);
      setStoredImpersonation({
        ticket: result.ticket,
        sessionId: result.sessionId,
        targetId: result.target.id,
        targetName: result.target.displayName,
        targetEmail: result.target.email,
        expiresAt: result.expiresAt,
      });
      clearPersistedQueryCache();
      window.location.reload();
    } catch (error) {
      appNotify.error(`Не удалось войти как ${displayName}`);
      throw error;
    }
  }, []);
}

export async function stopImpersonation(): Promise<void> {
  const stored = getStoredImpersonation();
  if (!stored) return;

  try {
    await impersonationService.stop(stored.sessionId);
  } catch {
    // Сессия могла уже истечь/быть закрытой на сервере — всё равно чистим локально.
  } finally {
    clearStoredImpersonation();
    clearPersistedQueryCache();
    window.location.reload();
  }
}
